import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Check, X, Crown, Users, Zap, Shield } from 'lucide-react';
import { subscriptionService } from '@/utils/subscriptionService';
import { MODULE_ACCESS, MODULE_DISPLAY_NAMES } from '@/constants/moduleAccess';

export const AccessControlDebugger = () => {
  const [currentPlan, setCurrentPlan] = useState<string>('');
  const [isSuperAdmin, setIsSuperAdmin] = useState<boolean>(false);
  const [moduleAccess, setModuleAccess] = useState<Record<string, boolean>>({});
  const [loading, setLoading] = useState(true);
  const [subscriptionStatus, setSubscriptionStatus] = useState<any>(null);
  const [hasAccess, setHasAccess] = useState<boolean>(false);

  useEffect(() => {
    checkAccessAndLoadData();
  }, []);

  const checkAccessAndLoadData = async () => {
    try {
      // First check if user is SuperAdmin
      const superAdminStatus = await subscriptionService.isUserSuperAdmin();
      setHasAccess(superAdminStatus);
      
      if (!superAdminStatus) {
        setLoading(false);
        return;
      }
      
      // Only load data if user has access
      await loadAccessControlData();
    } catch (error) {
      console.error('Error checking access:', error);
      setHasAccess(false);
      setLoading(false);
    }
  };

  const loadAccessControlData = async () => {
    try {
      setLoading(true);
      
      // Get all data in parallel for better performance
      const [planName, superAdminStatus, status] = await Promise.all([
        subscriptionService.getPlanName(),
        subscriptionService.isUserSuperAdmin(),
        subscriptionService.getSubscriptionStatus()
      ]);
      
      setCurrentPlan(planName);
      setIsSuperAdmin(superAdminStatus);
      setSubscriptionStatus(status);
      
      // Check access for all modules in parallel
      const moduleAccessPromises = Object.values(MODULE_ACCESS).map(async (moduleKey) => {
        const hasAccess = await subscriptionService.hasModuleAccess(moduleKey);
        return [moduleKey, hasAccess] as [string, boolean];
      });
      
      const accessResultsArray = await Promise.all(moduleAccessPromises);
      const accessResults: Record<string, boolean> = {};
      accessResultsArray.forEach(([moduleKey, hasAccess]) => {
        accessResults[moduleKey] = hasAccess;
      });
      
      setModuleAccess(accessResults);
      
    } catch (error) {
      console.error('Error loading access control data:', error);
    } finally {
      setLoading(false);
    }
  };

  const refreshData = () => {
    subscriptionService.clearCache();
    loadAccessControlData();
  };

  const getPlanIcon = (plan: string) => {
    switch (plan.toLowerCase()) {
      case 'individual':
        return <Zap className="h-4 w-4" />;
      case 'corporate':
        return <Users className="h-4 w-4" />;
      case 'enterprise':
        return <Crown className="h-4 w-4" />;
      case 'superadmin':
        return <Crown className="h-4 w-4 text-purple-600" />;
      default:
        return <Zap className="h-4 w-4" />;
    }
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Access Control Debugger</CardTitle>
          <CardDescription>Loading access control data...</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center p-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Don't render anything for non-SuperAdmin users
  if (!hasAccess) {
    return null;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Shield className="h-5 w-5" />
          Access Control Debugger
        </CardTitle>
        <CardDescription>
          Current subscription and module access status
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Current Plan Status */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold">Current Plan</h3>
            <Button variant="outline" size="sm" onClick={refreshData}>
              Refresh
            </Button>
          </div>
          
          <div className="flex items-center gap-3 p-4 border rounded-lg">
            {getPlanIcon(currentPlan)}
            <div>
              <div className="font-medium">{currentPlan}</div>
              <div className="text-sm text-gray-600">
                {subscriptionStatus?.isActive ? 'Active' : 'Inactive'} - 
                Started: {new Date(subscriptionStatus?.startDate || Date.now()).toLocaleDateString()}
              </div>
            </div>
            {isSuperAdmin && (
              <Badge variant="secondary" className="ml-auto">
                SuperAdmin
              </Badge>
            )}
          </div>
        </div>

        {/* Module Access */}
        <div className="space-y-4">
          <h3 className="font-semibold">Module Access</h3>
          <div className="grid gap-3">
            {Object.entries(MODULE_ACCESS).map(([key, moduleName]) => {
              const hasAccess = moduleAccess[moduleName];
              const displayName = MODULE_DISPLAY_NAMES[moduleName as keyof typeof MODULE_DISPLAY_NAMES] || 
                                moduleName.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
              
              return (
                <div key={moduleName} className="flex items-center justify-between p-3 border rounded-lg">
                  <div className="flex items-center gap-3">
                    {hasAccess ? (
                      <Check className="h-4 w-4 text-green-500" />
                    ) : (
                      <X className="h-4 w-4 text-red-500" />
                    )}
                    <span className="font-medium">{displayName}</span>
                  </div>
                  <Badge variant={hasAccess ? "default" : "secondary"}>
                    {hasAccess ? "Access" : "No Access"}
                  </Badge>
                </div>
              );
            })}
          </div>
        </div>

        {/* Additional Features */}
        <div className="space-y-4">
          <h3 className="font-semibold">Additional Features</h3>
          <div className="grid gap-3">
            <div className="flex items-center justify-between p-3 border rounded-lg">
              <span>Max Collaborators</span>
              <Badge variant="outline">
                {isSuperAdmin ? 'Unlimited' : '1'}
              </Badge>
            </div>
            <div className="flex items-center justify-between p-3 border rounded-lg">
              <span>Dedicated Support</span>
              <Badge variant={isSuperAdmin ? "default" : "secondary"}>
                {isSuperAdmin ? "Yes" : "No"}
              </Badge>
            </div>
            <div className="flex items-center justify-between p-3 border rounded-lg">
              <span>More Collaborators on Demand</span>
              <Badge variant={isSuperAdmin ? "default" : "secondary"}>
                {isSuperAdmin ? "Yes" : "No"}
              </Badge>
            </div>
          </div>
        </div>


      </CardContent>
    </Card>
  );
}; 